import React, { useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Chip, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails, Grid, Paper, Card, CardContent, CircularProgress, Alert, useTheme, useMediaQuery } from '@mui/material';
import { Icon } from '@iconify/react';
import { useToast } from './ui/ToastProvider';
interface ComplianceTransaction {
  txnId: string;
  mtoName: string;
  payoutType: 'FT' | 'IBFT' | 'OTC';
  beneficiaryName: string;
  beneficiaryCnic: string;
  beneficiaryTitle: string;
  amountFcy: string;
  currencyFcy: string;
  amountPkr: string;
  createdAt: string;
  lastUpdateAt: string;
  lifecycle: 'IN_REVIEW';
  currentState: 'SCREENING';
  mtoRef: string;
  bankRef: string;
  railRefs: {
    RaastMsgId?: string;
    RRN?: string;
    STAN?: string;
    UETR?: string;
  };
  screening: {
    engine: string;
    score: number;
    hits: string[];
    failReasonCode: string;
    failReasonText: string;
    screenedAt: string;
  };
  remitterName: string;
  remitterIdType: string;
  remitterIdNumber: string;
  remitterCountry: string;
  beneficiaryIban?: string;
  beneficiaryBank?: string;
  purposeCode: string;
  purposeText: string;
  fxRate: number;
}
interface CBSBeneficiaryDetails {
  cnic: string;
  fatherName: string;
  dob: string;
  accountOpeningDate: string;
  accountStatus: string;
}
interface ComplianceDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: ComplianceTransaction;
  onTransactionRemoval: (txnId: string) => void;
}
const ComplianceDetailsDrawer: React.FC<ComplianceDetailsDrawerProps> = ({
  open,
  onClose,
  transaction,
  onTransactionRemoval
}) => {
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'release' | 'fail'>('release');
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cbsDetails, setCbsDetails] = useState<CBSBeneficiaryDetails | null>(null);
  const [cbsLoading, setCbsLoading] = useState(false);
  const [cbsError, setCbsError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showToast
  } = useToast();
  const handleOpenActionDialog = (type: 'release' | 'fail') => {
    setActionType(type);
    setComment('');
    setCommentError('');
    setActionDialogOpen(true);
  };
  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setComment('');
    setCommentError('');
  };
  const validateComment = () => {
    if (!comment.trim()) {
      setCommentError('Comment is required');
      return false;
    }
    if (comment.trim().length < 10) {
      setCommentError('Comment must be at least 10 characters');
      return false;
    }
    setCommentError('');
    return true;
  };
  const handleActionConfirm = async () => {
    if (!validateComment()) return;
    setIsProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      if (actionType === 'release') {
        showToast('Transaction released successfully', 'success');
      } else {
        showToast('Transaction marked as failed', 'success');
      }
      onTransactionRemoval(transaction.txnId);
      handleCloseActionDialog();
    } catch (error) {
      showToast('Action failed. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };
  const handleFetchCBSDetails = async () => {
    setCbsLoading(true);
    setCbsError('');
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Mock CBS response
      const mockCBSDetails: CBSBeneficiaryDetails = {
        cnic: transaction.beneficiaryCnic,
        fatherName: 'Father Name Example',
        dob: '1985-06-15',
        accountOpeningDate: '2015-03-20',
        accountStatus: 'ACTIVE'
      };
      setCbsDetails(mockCBSDetails);
      showToast('Beneficiary details fetched successfully', 'success');
    } catch (error) {
      setCbsError('Failed to fetch beneficiary details from CBS');
      showToast('Failed to fetch CBS details', 'error');
    } finally {
      setCbsLoading(false);
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  const getPayoutTypeColor = (type: string) => {
    const colors = {
      FT: {
        bg: 'rgba(14, 165, 233, 0.1)',
        color: '#0EA5E9'
      },
      IBFT: {
        bg: 'rgba(139, 92, 246, 0.1)',
        color: '#8B5CF6'
      },
      OTC: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      }
    };
    return colors[type as keyof typeof colors];
  };
  return <>
      <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
      sx: {
        width: isMobile ? '100%' : 560,
        maxWidth: '100%'
      }
    }} data-id="element-575">
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }} data-id="element-576">
          {/* Header */}
          <Box sx={{
          p: 3,
          pb: 2
        }} data-id="element-577">
            <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2
          }} data-id="element-578">
              <Typography variant="h6" data-id="element-579">Compliance Review</Typography>
              <IconButton onClick={onClose} size="small" data-id="element-580">
                <Icon icon="lucide:x" width={20} data-id="element-581" />
              </IconButton>
            </Box>
            {/* Header Chips */}
            <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2
          }} data-id="element-582">
              <Chip label={transaction.payoutType} size="small" sx={{
              ...getPayoutTypeColor(transaction.payoutType),
              fontWeight: 600
            }} data-id="element-583" />
              <Chip label="IN_REVIEW" size="small" sx={{
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              color: '#F59E0B',
              fontWeight: 600
            }} data-id="element-584" />
              <Chip label="SCREENING" size="small" sx={{
              backgroundColor: 'rgba(100, 116, 139, 0.1)',
              color: '#64748B',
              fontWeight: 600
            }} data-id="element-585" />
              <Chip label={`${transaction.currencyFcy} ${transaction.amountFcy}`} size="small" sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              fontWeight: 600
            }} data-id="element-586" />
              <Chip label={`Local ${transaction.amountPkr}`} size="small" sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              fontWeight: 600
            }} data-id="element-587" />
            </Box>
            {/* Subheader */}
            <Box data-id="element-588">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom data-id="element-589">
                Remitting Institute (MTO)
              </Typography>
              <Typography variant="h6" color="primary.main" gutterBottom data-id="element-590">
                {transaction.mtoName}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-591">
                MTO Ref: {transaction.mtoRef} • Bank Ref: {transaction.bankRef}
              </Typography>
              {Object.values(transaction.railRefs).some(v => v) && <Typography variant="body2" color="text.secondary" data-id="element-592">
                  Rail Refs:{' '}
                  {[transaction.railRefs.RaastMsgId && `Raast: ${transaction.railRefs.RaastMsgId}`, transaction.railRefs.RRN && `RRN: ${transaction.railRefs.RRN}`, transaction.railRefs.STAN && `STAN: ${transaction.railRefs.STAN}`, transaction.railRefs.UETR && `UETR: ${transaction.railRefs.UETR}`].filter(Boolean).join(' • ')}
                </Typography>}
            </Box>
          </Box>
          <Divider data-id="element-593" />
          {/* Content */}
          <Box sx={{
          flex: 1,
          overflow: 'auto',
          p: 3
        }} data-id="element-594">
            {/* Screening Summary */}
            <Accordion defaultExpanded data-id="element-595">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-597" />} data-id="element-596">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-598">
                  <Icon icon="lucide:shield-alert" width={20} color="#F59E0B" data-id="element-599" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-600">
                    Screening Summary
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-601">
                <Alert severity="warning" sx={{
                mb: 2
              }} data-id="element-602">
                  <Typography variant="body2" fontWeight={600} data-id="element-603">
                    Status: REVIEW REQUIRED
                  </Typography>
                  <Typography variant="body2" sx={{
                  mt: 1
                }} data-id="element-604">
                    {transaction.screening.failReasonText}
                  </Typography>
                </Alert>
                <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }} data-id="element-605">
                  {transaction.screening.hits.map((hit, index) => <Paper key={index} variant="outlined" sx={{
                  p: 1.5,
                  backgroundColor: '#FEF3C7',
                  borderColor: '#F59E0B'
                }} data-id="element-606">
                      <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }} data-id="element-607">
                        <Icon icon="lucide:alert-circle" width={16} color="#F59E0B" data-id="element-608" />
                        <Typography variant="body2" data-id="element-609">{hit}</Typography>
                      </Box>
                    </Paper>)}
                </Box>
              </AccordionDetails>
            </Accordion>
            {/* Parties */}
            <Accordion data-id="element-610">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-612" />} data-id="element-611">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-613">
                  <Icon icon="lucide:users" width={20} color="#0EA5E9" data-id="element-614" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-615">
                    Parties
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-616">
                <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-617">
                  Remitter
                </Typography>
                <Paper variant="outlined" sx={{
                p: 2,
                mb: 2,
                borderRadius: 2
              }} data-id="element-618">
                  <Typography variant="body1" fontWeight={600} data-id="element-619">
                    {transaction.remitterName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                  mt: 1
                }} data-id="element-620">
                    {transaction.remitterIdType}: {transaction.remitterIdNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-id="element-621">
                    Country: {transaction.remitterCountry}
                  </Typography>
                </Paper>
                <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-622">
                  Beneficiary
                </Typography>
                <Paper variant="outlined" sx={{
                p: 2,
                borderRadius: 2
              }} data-id="element-623">
                  <Typography variant="body1" fontWeight={600} data-id="element-624">
                    {transaction.beneficiaryName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                  mt: 1
                }} data-id="element-625">
                    CNIC: {transaction.beneficiaryCnic}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" data-id="element-626">
                    Title: {transaction.beneficiaryTitle}
                  </Typography>
                  {transaction.beneficiaryIban && <Typography variant="body2" color="text.secondary" data-id="element-627">
                      IBAN: {transaction.beneficiaryIban}
                    </Typography>}
                  {transaction.beneficiaryBank && <Typography variant="body2" color="text.secondary" data-id="element-628">
                      Bank: {transaction.beneficiaryBank}
                    </Typography>}
                </Paper>
              </AccordionDetails>
            </Accordion>
            {/* Amounts & Purpose */}
            <Accordion data-id="element-629">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-631" />} data-id="element-630">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-632">
                  <Icon icon="lucide:banknote" width={20} color="#10B981" data-id="element-633" />
                  <Typography variant="subtitle2" fontWeight={600} data-id="element-634">
                    Amounts & Purpose
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-635">
                <Grid container spacing={2} data-id="element-636">
                  <Grid item xs={12} sm={6} data-id="element-637">
                    <Typography variant="caption" color="text.secondary" data-id="element-638">
                      Foreign Currency
                    </Typography>
                    <Typography variant="h6" color="primary.main" data-id="element-639">
                      {transaction.currencyFcy} {transaction.amountFcy}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} data-id="element-640">
                    <Typography variant="caption" color="text.secondary" data-id="element-641">
                      Local Amount
                    </Typography>
                    <Typography variant="h6" color="primary.main" data-id="element-642">
                      {transaction.amountPkr}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-id="element-643">
                    <Typography variant="caption" color="text.secondary" data-id="element-644">
                      Purpose
                    </Typography>
                    <Typography variant="body1" data-id="element-645">
                      {transaction.purposeText} ({transaction.purposeCode})
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            {/* Timeline */}
            <Accordion data-id="element-646">
              <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-648" />} data-id="element-647">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }} data-id="element-649">
                  <Box sx={{
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 82, 204, 0.1)'
                }} data-id="element-650">
                    <Icon icon="lucide:clock" width={20} color="#0052CC" data-id="element-651" />
                  </Box>
                  <Typography variant="subtitle1" data-id="element-652">Status History</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails data-id="element-653">
                <Box sx={{
                position: 'relative',
                pl: 3
              }} data-id="element-654">
                  <Box sx={{
                  position: 'absolute',
                  left: 8,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  backgroundColor: '#E2E8F0'
                }} data-id="element-655" />
                  <Box sx={{
                  mb: 2,
                  position: 'relative'
                }} data-id="element-656">
                    <Box sx={{
                    position: 'absolute',
                    left: -19,
                    top: 4,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    border: '2px solid white'
                  }} data-id="element-657" />
                    <Typography variant="caption" color="text.secondary" data-id="element-658">
                      {formatDate(transaction.createdAt)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} data-id="element-659">
                      Transaction Received
                    </Typography>
                  </Box>
                  <Box sx={{
                  position: 'relative'
                }} data-id="element-660">
                    <Box sx={{
                    position: 'absolute',
                    left: -19,
                    top: 4,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#F59E0B',
                    border: '2px solid white'
                  }} data-id="element-661" />
                    <Typography variant="caption" color="text.secondary" data-id="element-662">
                      {formatDate(transaction.screening.screenedAt)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} data-id="element-663">
                      Screening (REVIEW)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontSize="0.8rem" data-id="element-664">
                      Flagged for manual review
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            {/* CBS Details (FT only) */}
            {transaction.payoutType === 'FT' && <Box sx={{
            mt: 3
          }} data-id="element-665">
                <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1
            }} data-id="element-666">
                  <Box sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 82, 204, 0.1)'
              }} data-id="element-667">
                    <Icon icon="lucide:building-2" width={20} color="#0052CC" data-id="element-668" />
                  </Box>
                  <Typography variant="subtitle1" data-id="element-669">Partner Details</Typography>
                </Box>
                {!cbsDetails && !cbsError && <Button variant="outlined" fullWidth startIcon={cbsLoading ? <CircularProgress size={16} data-id="element-671" /> : <Icon icon="lucide:download" width={16} data-id="element-672" />} onClick={handleFetchCBSDetails} disabled={cbsLoading} data-id="element-670">
                    {cbsLoading ? 'Fetching...' : 'Fetch Beneficiary Details from CBS'}
                  </Button>}
                {cbsError && <Alert severity="error" sx={{
              mb: 2
            }} data-id="element-673">
                    {cbsError}
                  </Alert>}
                {cbsDetails && <Card variant="outlined" sx={{
              backgroundColor: '#F8FAFC'
            }} data-id="element-674">
                    <CardContent data-id="element-675">
                      <Grid container spacing={2} data-id="element-676">
                        <Grid item xs={12} sm={6} data-id="element-677">
                          <Typography variant="caption" color="text.secondary" data-id="element-678">
                            CNIC
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" data-id="element-679">
                            {cbsDetails.cnic}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-680">
                          <Typography variant="caption" color="text.secondary" data-id="element-681">
                            Father Name
                          </Typography>
                          <Typography variant="body2" data-id="element-682">
                            {cbsDetails.fatherName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-683">
                          <Typography variant="caption" color="text.secondary" data-id="element-684">
                            Date of Birth
                          </Typography>
                          <Typography variant="body2" data-id="element-685">
                            {new Date(cbsDetails.dob).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-686">
                          <Typography variant="caption" color="text.secondary" data-id="element-687">
                            Account Opening Date
                          </Typography>
                          <Typography variant="body2" data-id="element-688">
                            {new Date(cbsDetails.accountOpeningDate).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} data-id="element-689">
                          <Typography variant="caption" color="text.secondary" data-id="element-690">
                            Account Status
                          </Typography>
                          <Chip label={cbsDetails.accountStatus} size="small" sx={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10B981',
                      fontWeight: 600,
                      mt: 0.5
                    }} data-id="element-691" />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>}
              </Box>}
          </Box>
          <Divider data-id="element-692" />
          {/* Actions */}
          <Box sx={{
          p: 3,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap'
        }} data-id="element-693">
            <Button variant="contained" fullWidth={isMobile} startIcon={<Icon icon="lucide:check" width={16} data-id="element-695" />} onClick={() => handleOpenActionDialog('release')} sx={{
            flex: isMobile ? undefined : 1
          }} data-id="element-694">
              Manually Release
            </Button>
            <Button variant="outlined" color="error" fullWidth={isMobile} startIcon={<Icon icon="lucide:x" width={16} data-id="element-697" />} onClick={() => handleOpenActionDialog('fail')} sx={{
            flex: isMobile ? undefined : 1
          }} data-id="element-696">
              Mark as Failed
            </Button>
          </Box>
        </Box>
      </Drawer>
      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={handleCloseActionDialog} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 2
      }
    }} data-id="element-698">
        <DialogTitle data-id="element-699">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-700">
            <Icon icon={actionType === 'release' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'release' ? '#10B981' : '#EF4444'} data-id="element-701" />
            <Typography variant="h6" data-id="element-702">
              {actionType === 'release' ? 'Manually Release Transaction' : 'Mark Transaction as Failed'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-703">
          <Typography variant="body2" color="text.secondary" sx={{
          mb: 2
        }} data-id="element-704">
            {actionType === 'release' ? `Approve transaction ${transaction.txnId} for processing? This will bypass screening alerts.` : `Mark transaction ${transaction.txnId} as failed? This action will reject the transaction.`}
          </Typography>
          <TextField fullWidth multiline rows={4} label="Comment (Required)" placeholder="Enter your reason for this action (minimum 10 characters)..." value={comment} onChange={e => {
          setComment(e.target.value);
          if (commentError) setCommentError('');
        }} error={!!commentError} helperText={commentError || `${comment.length} characters`} required data-id="element-705" />
        </DialogContent>
        <DialogActions sx={{
        p: 3,
        pt: 0
      }} data-id="element-706">
          <Button onClick={handleCloseActionDialog} disabled={isProcessing} data-id="element-707">
            Cancel
          </Button>
          <Button variant="contained" color={actionType === 'release' ? 'primary' : 'error'} onClick={handleActionConfirm} disabled={isProcessing || !comment.trim() || comment.trim().length < 10} startIcon={isProcessing ? <CircularProgress size={16} data-id="element-709" /> : undefined} data-id="element-708">
            {isProcessing ? 'Processing...' : actionType === 'release' ? 'Release' : 'Mark as Failed'}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default ComplianceDetailsDrawer;